// File: src/context/AuthContext.jsx

import { createContext, useState, useEffect, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = "https://globisun-app.onrender.com/api";

  // Always send credentials with requests
  axios.defaults.withCredentials = true;

  // ✅ Load session from localStorage when the app starts
  useEffect(() => {
    try {
      const savedToken = localStorage.getItem("token");
      const savedUser = localStorage.getItem("user");

      if (savedToken && savedUser) {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
      }
    } catch (err) {
      console.error("⚠️ Failed to restore session:", err);
    } finally {
      // Delay a bit to ensure localStorage is available (important for WebView)
      setTimeout(() => setLoading(false), 500);
    }
  }, []);

  // ✅ Keep Axios Authorization header synced with token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // ✅ Login function
  const login = async ({ email, password }) => {
    try {
      const res = await axios.post(`${API_BASE}/auth/login`, { email, password });
      const userData = res.data.user;
      const jwtToken = res.data.token;

      // Persist in localStorage
      localStorage.setItem("token", jwtToken);
      localStorage.setItem("user", JSON.stringify(userData));

      setUser(userData);
      setToken(jwtToken);

      axios.defaults.headers.common["Authorization"] = `Bearer ${jwtToken}`;
    } catch (err) {
      console.error("❌ Login failed:", err.response?.data?.msg || err.message);
      throw new Error(err.response?.data?.msg || "Login failed");
    }
  };

  // ✅ Logout function
  const logout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      setToken(null);
      delete axios.defaults.headers.common["Authorization"];
    } catch (err) {
      console.warn("⚠️ Failed to clear storage on logout:", err);
    }
  };

  // ✅ Refresh user data from server
  const refreshUserData = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_BASE}/auth/me`);
      if (res.status === 200 && res.data) {
        setUser(res.data);
        localStorage.setItem("user", JSON.stringify(res.data));
      }
    } catch (err) {
      console.error("🔁 Failed to refresh user:", err.response?.data?.msg || err.message);
    }
  };

  // ✅ Manual user update helper
  const updateUser = (newUserData) => {
    setUser(newUserData);
    localStorage.setItem("user", JSON.stringify(newUserData));
  };

  // ✅ Prevent app from rendering before localStorage restores (WebView fix)
  if (loading) return null;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        refreshUserData,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ✅ ProtectedRoute component
export const ProtectedRoute = ({ children }) => {
  const { user, token } = useContext(AuthContext);
  const location = useLocation();

  if (!user || !token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};
